import { DataGridPremium as DataGrid, useGridApiRef } from "@mui/x-data-grid-premium";
import { useState, useMemo, useCallback, useEffect } from "react";
import DotMenu from "../DotMenu";
import {
  getGridColDefForArrayValues,
  getGridColDefForDateRange,
  getGridColDefForSingleSelect,
  IconAndLabelColumnHeader,
} from "../../utils/dataGrid";
import { CustomNoRowsOverlay } from "../../utils/StyledGridOverlay";
import { dataGridLang } from "../MemToolBar";
import { ToolBar } from "../ToolBar";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import ListAltIcon from "@mui/icons-material/ListAlt";
import DomainIcon from "@mui/icons-material/Domain";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EditIcon from "@mui/icons-material/Edit";
import OrderFormBase from "./OrderFormBase";
import Translate from "../../utils/Translate";
import { useApi } from "../../utils/Api";
import DoneIcon from "@mui/icons-material/Done";
import { Box, Dialog, IconButton, Stack, Toolbar, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { formatDate } from "../../utils/dateTimeFormat";
import { useNavigate, useSearchParams } from "react-router-dom";
import usePagination from "../../utils/pagination";
import { accessKeys, hasAccess } from "../../utils/userAccess";
import DataGridEasyFilters from "../DataGridEasyFilters";
import LinkMenu from "../LinkMenu";
import ClearIcon from "@mui/icons-material/Clear";
import { getOrderIconbox, orderStatus } from "../../utils/order";
import {
  AccessTime,
  Block,
  Category,
  Check,
  Event,
  Flag,
  FormatListNumberedRtl,
  Inventory,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Merge,
  Money,
  MoneyOff,
  Pin,
  RequestQuote,
  School,
  Undo,
  Warning,
} from "@mui/icons-material";
import { groupBy } from "lodash";
import { AlertDialog } from "../AlertDialog";
import OrdersGridDetailsPanel from "./OrdersGridDetailsPanel";
import { formatMoney } from "../../utils/formatMoney";
import { getIcon, priority } from "../../utils/priority";
import PickOrderDialog from "../pick/PickOrderDialog";
import {
  isAutoPickPartType,
  isCardPrintPartType,
  isCoursePartType,
  isFreightPartType,
  isPhysicalPickPartType,
  partType,
} from "../../utils/part";
import MergeOrdersDialog from "./MergeOrdersDialog";
import { useSnackbar } from "notistack";
import { paymentStatus } from "../../utils/payment";
import OrderScheduleForInvoiceDialog from "./OrderScheduleForInvoiceDialog";
import YesOrNoDialog from "../YesOrNoDialog";

const defaultFilterPicking = {
  filterModel: {
    items: [
      {
        id: "default-picking-status",
        field: "status",
        operator: "between",
        value: [orderStatus.ordered, orderStatus.underPicking],
      },
    ],
    logicOperator: "and",
  },
  sortModel: [
    {
      field: "priority",
      sort: "desc",
    },
    {
      field: "pickDate",
      sort: "asc",
    },
  ],
};

const defaultFilters = [
  {
    id: "default-current-orders",
    nameTranslationKey: "CurrentOrdersFilterLabel",
    descriptionTranslationKey: "DefaultCurrentOrdersFilterDescription",
    filterModel: {
      items: [
        {
          id: "default-current-orders-status",
          field: "status",
          operator: "between",
          value: [orderStatus.ordered, orderStatus.partlyPaid],
        },
        {
          id: "default-current-orders-pickDate",
          field: "pickDate",
          operator: "between",
          // Up to and including one week from now
          value: [null, new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)],
        },
      ],
      logicOperator: "and",
    },
    sortModel: [
      {
        field: "priority",
        sort: "desc",
      },
      {
        field: "pickDate",
        sort: "desc",
      },
    ],
  },
  {
    ...defaultFilterPicking,
    id: "default-picking",
    nameTranslationKey: "PickFilterLabel",
    descriptionTranslationKey: "DefaultPickFilterDescription",
  },
  {
    ...defaultFilterPicking,
    id: "default-picking-licenses",
    nameTranslationKey: "PickLicensesFilterLabel",
    descriptionTranslationKey: "DefaultPickLicensesFilterDescription",
    filterModel: {
      ...defaultFilterPicking.filterModel,
      items: [
        ...defaultFilterPicking.filterModel.items,
        {
          id: "default-picking-licenses-pickDate",
          field: "partTypes",
          operator: "contains",
          value: [partType.plasticCardPrintJob, partType.plasticCardPrintJobAndDigitalLicense],
        },
      ],
    },
  },
  {
    ...defaultFilterPicking,
    id: "default-picking-books",
    nameTranslationKey: "PickOtherFilterLabel",
    descriptionTranslationKey: "DefaultPickOtherFilterDescription",
    filterModel: {
      ...defaultFilterPicking.filterModel,
      items: [
        ...defaultFilterPicking.filterModel.items,
        {
          id: "default-picking-books-partTypes",
          field: "partTypes",
          operator: "contains",
          value: [partType.book, partType.storeGoods],
        },
      ],
    },
  },
  {
    ...defaultFilterPicking,
    id: "default-picking-misc",
    nameTranslationKey: "PickMiscFilterLabel",
    descriptionTranslationKey: "DefaultPickMiscFilterDescription",
    filterModel: {
      ...defaultFilterPicking.filterModel,
      items: [
        ...defaultFilterPicking.filterModel.items,
        {
          id: "default-picking-misc-partTypes",
          field: "partTypes",
          operator: "excluding",
          value: [
            partType.plasticCardPrintJob,
            partType.plasticCardPrintJobAndDigitalLicense,
            partType.book,
            partType.storeGoods,
            partType.freight,
            partType.freight06Vat,
            partType.freight12Vat,
            partType.freight25Vat,
            partType.freightEUVat,
            partType.freightOutsideEUVat,
          ],
        },
      ],
    },
  },
  {
    id: "default-ready-for-invoice-filter",
    nameTranslationKey: "ReadyForInvoice",
    descriptionTranslationKey: "DefaultReadyForInvoiceFilterDescription",
    filterModel: {
      items: [
        {
          id: "default-ready-for-invoice-filter-status",
          field: "status",
          operator: "between",
          rangeOperator: "all",
          value: [orderStatus.picked, orderStatus.pickedAutoOrder],
        },
        {
          id: "default-ready-for-invoice-filter-paymentStatus-1",
          field: "paymentStatus",
          operator: "is",
          value: paymentStatus.readyForInvoice,
        },
        {
          id: "default-ready-for-invoice-filter-paymentStatus-2",
          field: "paymentStatus",
          operator: "is",
          value: paymentStatus.directPaidReadyForInvoice,
        },
      ],
      logicOperator: "or",
    },
    sortModel: [
      {
        field: "regDate",
        sort: "asc",
      },
    ],
  },
  {
    id: "default-scheduled-for-invoice-filter",
    nameTranslationKey: "ScheduledInvoiceDate",
    descriptionTranslationKey: "DefaultReadyForInvoiceFilterDescription",
    filterModel: {
      items: [
        {
          id: "default-scheduled-for-invoice-filter-status",
          field: "status",
          operator: "is",
          value: orderStatus.scheduledForInvoice,
        },
      ],
      logicOperator: "and",
    },
    sortModel: [
      {
        field: "scheduledInvoiceDate",
        sort: "asc",
      },
    ],
  },
  {
    id: "default-is-course-filter",
    nameTranslationKey: "Courses",
    descriptionTranslationKey: "DefaultCoursesFilterDescription",
    filterModel: {
      items: [
        {
          id: "default-is-course-filter-isCourse",
          field: "isCourse",
          operator: "is",
          value: true,
        },
      ],
      logicOperator: "and",
    },
  },
  {
    id: "default-missing-info",
    nameTranslationKey: "OrderStatus_MissingInfo",
    descriptionTranslationKey: "DefaultMissingInfoFilterDescription",
    filterModel: {
      items: [
        {
          id: "default-missing-info-isMissingInfo",
          field: "isMissingInfo",
          operator: "is",
          value: true,
        },
      ],
      logicOperator: "and",
    },
  },
  {
    id: "default-invoice-canceled",
    nameTranslationKey: "InvoicedCanceledInERP",
    descriptionTranslationKey: "DefaultInvoicedCanceledInERPDescription",
    filterModel: {
      items: [
        {
          id: "default-invoice-canceled-invoiceCanceled",
          field: "invoiceCanceled",
          operator: "is",
          value: true,
        },
      ],
      logicOperator: "and",
    },
  },
];

const defaultFilterModelAll = defaultFilters.find((x) => x.id === "default-current-orders").filterModel;
const defaultSortModelAll = defaultFilters.find((x) => x.id === "default-current-orders").sortModel;
const defaultFilterModelCompany = {
  items: [],
  logicOperator: "and",
};
const defaultSortModelCompany = [
  {
    field: "pickDate",
    sort: "desc",
  },
];

const getStatusFilterOptions = () =>
  Object.values(orderStatus).map((status) => ({
    value: status,
    label: Translate.getOrderStatus(status),
  }));

const excludedPartTypes = [partType.eLearning, partType.extraFinalExam];
const getPartTypesFilterOptions = () =>
  Object.values(partType)
    .filter((partType) => !excludedPartTypes.includes(partType))
    .map((partType) => ({
      value: partType,
      label: Translate.getPartType(partType),
    }))
    .filter((option) => !option.label.includes("N/A"));

function buildPaginationFilter(courseId) {
  if (!courseId) {
    return null;
  }

  return {
    items: [
      {
        id: "course-filter",
        field: "courseId",
        operator: "=",
        value: courseId.toString(),
      },
    ],
    logicOperator: "and",
  };
}

export default function OrdersGrid({ actorId, searchText, onLoaded }) {
  const [selectedOrderIds, setSelectedOrderIds] = useState({ type: "include", ids: new Set() });
  const [pickOrderDetails, setPickOrderDetails] = useState(null);
  const [editOrderDetails, setEditOrderDetails] = useState(null);
  const [scheduleOrderInvoiceDetails, setScheduleOrderInvoiceDetails] = useState(null);
  const [invoiceOrderQuestionDetails, setInvoiceOrderQuestionDetails] = useState(null);
  const [paginationStateId] = useState(actorId ? `COMPANY_${actorId}_ORDERS_GRID` : "ORDERS_GRID");
  const [defaultFilterModel] = useState(actorId ? defaultFilterModelCompany : defaultFilterModelAll);
  const [defaultSortModel] = useState(actorId ? defaultSortModelCompany : defaultSortModelAll);
  const [showForceCancelOrderId, setShowForceCancelOrderId] = useState(null);

  const [longErrorMessage, setLongErrorMessage] = useState(null);
  const [ordersToMerge, setOrdersToMerge] = useState([]);
  const [refreshTimeStamp, setRefreshTimeStamp] = useState(new Date());

  const localizedTextsMap = dataGridLang();
  const api = useApi();
  const apiRef = useGridApiRef();

  const breakpoint = actorId ? 800 : 900;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(breakpoint));
  const gridClassName = isSmallScreen ? "dgSmall" : "stdVisible dgDynIconText shiftDatagridToolbar";

  const [searchParams] = useSearchParams();
  const filterCourseId = searchParams.get("filterCourseId");

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const navigate = useNavigate();
  const navigateToCompany = useCallback(
    (id) => {
      navigate(`/dashboard/company/${id}`, { replace: false });
    },
    [navigate],
  );
  const navigateToCourse = useCallback(
    (id) => {
      navigate(`/dashboard/course/${id}`, { replace: false });
    },
    [navigate],
  );

  const loadOrders = useCallback(
    async (queryModel) => {
      const url = actorId
        ? `${import.meta.env.VITE_MAIN_URL}actors/${actorId}/orders/get-page`
        : `${import.meta.env.VITE_MAIN_URL}orders/get-page`;

      const response = await api.fetch(url, queryModel, "POST");
      setRefreshTimeStamp(new Date());
      if (onLoaded) {
        onLoaded();
      }

      if (response && response.isSuccessful) {
        setSelectedOrderIds({ type: "include", ids: new Set() }); // Let's clear the selection on data load
        return { rows: response.rows, rowCount: response.rowCount };
      }
    },
    [api, onLoaded, actorId],
  );

  const { localUpdateRows, reloadData, resetPagination, ...pagination } = usePagination(
    loadOrders,
    searchText,
    defaultSortModel,
    defaultFilterModel,
    [50, 75, 100],
    paginationStateId,
    buildPaginationFilter(filterCourseId),
  );

  useEffect(() => {
    if (isSmallScreen) {
      // Most columns will not be available on small screens, so reset
      // the pagination, in case there was any saved state in session
      resetPagination();
    }
  }, [resetPagination, isSmallScreen]);

  const forceCancelOrder = useCallback(
    async (orderId) => {
      const response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}orders/${orderId}/force-cancel`, true, "POST");

      if (response.isSuccessful) {
        const successMessage = enqueueSnackbar(Translate.get("Ready"), {
          variant: "success",
          autoHideDuration: 6000,
          onClick: () => closeSnackbar(successMessage),
        });
        reloadData();
      }
    },
    [api, reloadData],
  );

  const invoiceOrder = useCallback(
    async (orderId, isMulti) => {
      const response = await api.fetchWithOverride(
        `${import.meta.env.VITE_MAIN_URL}orders/${orderId}/invoice`,
        true,
        "POST",
        (response) => (isMulti ? true : response.isSuccessful),
      );

      if (response.isSuccessful) {
        localUpdateRows((prev) => {
          const index = prev.findIndex((o) => o.orderId === orderId);
          return index >= 0
            ? prev.with(index, {
                ...prev[index],
                status: response.status,
                paymentStatus: response.paymentStatus,
              })
            : prev;
        });
      }
      return response;
    },
    [api, localUpdateRows],
  );

  const handleInvoiceOrder = useCallback(
    async (order, isMulti, force) => {
      const askAboutFreight = !force && mayNeedFreightBeforeInvoice(order);

      if (askAboutFreight && isMulti) {
        // Should already be force==true, if we are here something went wrong
        throw new Error("handleInvoiceOrder error");
      }

      if (askAboutFreight) {
        setInvoiceOrderQuestionDetails({ order });
      } else {
        const result = await invoiceOrder(order.orderId, isMulti);
        if (!isMulti && result.isSuccessful) {
          // om isMulti hanteras meddelande och omladdning i handleMultiDotMenuActionOnClick(...)
          const successMessage = enqueueSnackbar(Translate.get("OrderHasBeenInvoiced"), {
            variant: "success",
            autoHideDuration: 6000,
            onClick: () => closeSnackbar(successMessage),
          });
          reloadData();
        } else if (!isMulti && !result.isSuccessful && result.userMessage) {
          setLongErrorMessage(result.userMessage);
        }
        return result;
      }
    },
    [invoiceOrder, reloadData, enqueueSnackbar, closeSnackbar],
  );

  const handleScheduleInvoiceClose = useCallback(
    async (didUpdate) => {
      if (didUpdate) {
        const successMessage = enqueueSnackbar(Translate.get("Ready"), {
          variant: "success",
          autoHideDuration: 6000,
          onClick: () => closeSnackbar(successMessage),
        });
        reloadData();
      }
      setScheduleOrderInvoiceDetails(null);
    },
    [localUpdateRows],
  );

  const finishOrder = useCallback(
    async (orderId, isMulti) => {
      const response = await api.fetchWithOverride(
        `${import.meta.env.VITE_MAIN_URL}orders/${orderId}/finish`,
        true,
        "POST",
        (response) => (isMulti ? true : response.isSuccessful),
      );

      if (response.isSuccessful) {
        localUpdateRows((prev) => {
          const index = prev.findIndex((o) => o.orderId === orderId);
          return index >= 0
            ? prev.with(index, {
                ...prev[index],
                status: orderStatus.finished,
              })
            : prev;
        });
      }
      return response;
    },
    [api, localUpdateRows],
  );

  const changeOrderPriority = useCallback(
    async (orderId, priorityParam, isMulti) => {
      const response = await api.fetchWithOverride(
        `${import.meta.env.VITE_MAIN_URL}orders/${orderId}/priority?priority=${priorityParam}`,
        true,
        "POST",
        (response) => (isMulti ? true : response.isSuccessful),
      );
      if (response.isSuccessful) {
        localUpdateRows((prev) => {
          const index = prev.findIndex((o) => o.orderId === orderId);
          return index >= 0
            ? prev.with(index, {
                ...prev[index],
                priority: priorityParam,
              })
            : prev;
        });
      }
      return response;
    },
    [api, localUpdateRows],
  );

  const undoStartPick = useCallback(
    async (orderId, isMulti) => {
      const response = await api.fetch(
        `${import.meta.env.VITE_MAIN_URL}orders/${orderId}/undo-start-pick`,
        true,
        "POST",
      );
      if (response.isSuccessful) {
        reloadData();
      }
      return response;
    },
    [api, reloadData],
  );

  const canMergeOrders = useCallback(
    (orders) => {
      if (orders.some((o) => o.isPrepay || !!o.paymentStatus)) {
        // Not neccessarily impossible to support. But it might not be improtant enough to warrant the difficulties.
        // If we want to add support it should be possible to do with orderStatus 19 and paymentStatus 20.
        return { canMerge: false, error: "MergingPrepayNotSupported" };
      }

      const firstOrder = orders[0];
      if (orders.some((o) => o.actorId !== firstOrder.actorId)) {
        return { canMerge: false, error: "NotSameCompany" };
      } else if (orders.some((o) => o.status < orderStatus.ordered || o.status >= orderStatus.waitingForInvoice)) {
        return { canMerge: false, error: "WrongStatus" };
      } else if (orders.some((o) => o.currency !== firstOrder.currency)) {
        return { canMerge: false, error: "NotSameCurrency" };
      } else if (orders.some((o) => o.receiverActorId !== firstOrder.receiverActorId)) {
        return { canMerge: false, error: "NotSameReceiverCompany" };
      }
      return { canMerge: true };
    },
    [pagination.rows],
  );

  const getDotMenuActions = useCallback(
    (order, isMulti, multiOrderCount) => {
      const menudata = [];
      menudata.push({
        id: "edit-order",
        hide: isMulti,
        icon: <EditIcon />,
        text: Translate.get("EditOrder"),
        customClass: hasAccess(accessKeys.isMASystem) ? "bigCourseDialog" : "mediumCourseDialog",
        onClick: () => setEditOrderDetails({ orderId: order.orderId }),
      });

      if (hasAccess(accessKeys.isMASystem)) {
        // Order actions based on status
        if (order.status >= orderStatus.ordered && order.status <= orderStatus.underPicking) {
          const hasStartedPick = order.orderLines.some((ol) => ol.status >= orderStatus.underPicking);
          menudata.push({
            id: "pick-order",
            hide: isMulti,
            disabled: order.priority === priority.onHold,
            icon: <Inventory />,
            text: hasStartedPick ? Translate.get("ContinuePick") : Translate.get("StartPick"),
            onClick: () => setPickOrderDetails({ orderId: order.orderId, hasStartedPick }),
          });

          if (hasStartedPick) {
            const canUndoStartPick = order.orderLines.every((ol) =>
              !isAutoPickPartType(ol.part.partType) && !isFreightPartType(ol.part.partType)
                ? ol.status === orderStatus.underPicking && ol.delQty === 0
                : true,
            );
            menudata.push({
              id: "undo-pick-order",
              disabled: !canUndoStartPick || order.priority === priority.onHold,
              icon: <Undo />,
              text: Translate.get("UndoStartPick"),
              onClick: async () => await undoStartPick(order.orderId),
            });
          }
        } else if (
          order.isPrepay &&
          order.status == orderStatus.waitingForPrePayment &&
          (order.paymentStatus == paymentStatus.readyForInvoice ||
            order.paymentStatus == paymentStatus.directPaidReadyForInvoice)
        ) {
          menudata.push({
            id: "prepay-invoice-order",
            icon: <RequestQuote />,
            // Prepay orders can be invoiced right after order creation, so I think it's better to be
            // cautious and not allow doing that as a multi action, to avoid accidental invoicing
            hide: isMulti,
            disabled:
              order.priority === priority.onHold ||
              (order.actorCustomerOrderNoRequired && !order.customerOrderNo && order.invoicePrice > 0),
            text: `${Translate.get("Invoice")} (${Translate.get("PrePayment").toLowerCase()})`,
            onClick: async () => await handleInvoiceOrder(order, false, false),
          });
        } else if (order.status >= orderStatus.waitingForCourseInvoice && order.status <= orderStatus.pickedAutoOrder) {
          const hasCourse = order.orderLines.some((ol) => isCoursePartType(ol.part.partType));
          const isPicked = order.orderLines.every(
            (ol) => ol.status === orderStatus.picked || ol.status == orderStatus.pickedAutoOrder,
          );

          if (isPicked && !hasCourse) {
            // After finishing pick some actions are show in the dialog.
            // But sometimes we might need to perform the actions later,
            // for example if the dialog was accidentally closed.
            menudata.push({
              id: "pick-order",
              hide: isMulti,
              disabled: order.priority === priority.onHold,
              icon: <Inventory />,
              text: Translate.get("AfterPick"),
              onClick: () =>
                setPickOrderDetails({
                  orderId: order.orderId,
                  hasStartedPick: true,
                }),
            });
          }

          if (!order.isPrepay) {
            // Prepay orders have a separate invoice action above
            menudata.push({
              id: "invoice-order",
              icon: <RequestQuote />,
              disabled:
                order.priority === priority.onHold ||
                (order.actorCustomerOrderNoRequired && !order.customerOrderNo && order.invoicePrice > 0),
              text: Translate.get("Invoice"),
              multiVerifyActionQuestion:
                multiOrderCount > 1
                  ? Translate.get("MultiInvoiceOrderWithPhysicalPickNoFreight")
                  : Translate.get("InvoiceOrderWithPhysicalPickNoFreight"),
              onClick: async () => await handleInvoiceOrder(order, isMulti, isMulti ? true : false),
            });

            menudata.push({
              id: "schedule-invoice-order",
              icon: <Event />,
              hide: isMulti || !hasAccess(accessKeys.isMASystem),
              disabled:
                order.isPrepay ||
                order.priority === priority.onHold ||
                (order.actorCustomerOrderNoRequired && !order.customerOrderNo && order.invoicePrice > 0),
              text: Translate.get("ScheduleInvoice"),
              onClick: () => setScheduleOrderInvoiceDetails(order),
            });
          }
        } else if (order.status === orderStatus.scheduledForInvoice) {
          // Can be used to change/cancel scheduled invoicing
          menudata.push({
            id: "schedule-invoice-order",
            icon: <Event />,
            disabled: order.isPrepay,
            hide: isMulti || !hasAccess(accessKeys.isMASystem),
            text: Translate.get("EditScheduleInvoice"),
            onClick: () => setScheduleOrderInvoiceDetails(order),
          });
        } else if (order.status === orderStatus.paid) {
          menudata.push({
            id: "finish-order",
            icon: <DoneIcon />,
            disabled: order.priority === priority.onHold,
            text: Translate.get("FinishOrder"),
            onClick: async () => await finishOrder(order.orderId, isMulti),
          });
        }

        // Priority changes
        if (order.priority !== priority.high) {
          menudata.push({
            id: "high-priority",
            icon: getIcon(priority.high),
            disabled: false,
            text: `${Translate.get("Priority")}: ${Translate.getPriority(priority.high)}`,
            onClick: async () => await changeOrderPriority(order.orderId, priority.high, isMulti),
          });
        }
        if (order.priority && order.priority !== priority.normal) {
          menudata.push({
            id: "normal-priority",
            icon: getIcon(priority.normal),
            disabled: false,
            text: `${Translate.get("Priority")}: ${Translate.getPriority(priority.normal)}`,
            onClick: async () => await changeOrderPriority(order.orderId, priority.normal, isMulti),
          });
        }
        if (order.priority !== priority.onHold) {
          menudata.push({
            id: "onHold-priority",
            icon: getIcon(priority.onHold),
            disabled: false,
            text: `${Translate.get("Priority")}: ${Translate.getPriority(priority.onHold)}`,
            onClick: async () => await changeOrderPriority(order.orderId, priority.onHold, isMulti),
          });
        }
        if (!!order.invoiceCanceled) {
          menudata.push({
            id: "handle-invoice-canceled",
            icon: <Block />,
            disabled: !!order.paymentStatus
              ? order.paymentStatus < paymentStatus.sentToERP
              : order.status < orderStatus.sentToERP,
            text: Translate.get("HandleInvoiceCanceled"),
            onClick: () => setShowForceCancelOrderId(order.orderId),
          });
        }
      } else {
        // Payments are only used by MA system
        if (order.status == orderStatus.waitingForPrePayment) throw new Error("Unsupported order state");
        if (!!order.paymentStatus) throw new Error("Unsupported order state");

        menudata.push({
          id: "finish-order",
          icon: <DoneIcon />,
          disabled: order.status === orderStatus.finished,
          text: Translate.get("FinishOrder"),
          onClick: async () => await finishOrder(order.orderId, isMulti),
        });
      }

      if (order.courseId) {
        menudata.push({
          id: `go-to-course-${order.courseId}`,
          icon: <School />,
          text: Translate.get("GoToCourse"),
          onClick: () => navigateToCourse(order.courseId),
        });
      }

      return menudata;
    },
    [finishOrder, navigateToCourse, handleInvoiceOrder, changeOrderPriority, undoStartPick],
  );

  const getMultiMenuActions = useCallback(
    (orders) => {
      const menudata = [];
      if (hasAccess(accessKeys.isMASystem)) {
        const result = canMergeOrders(orders);
        const disabledTooltip = result.error
          ? `${Translate.get("CannotMergeOrdersReason")} ${Translate.get(result.error)}`
          : null;
        menudata.push({
          id: "merge-orders",
          icon: <Merge />,
          hide: orders.length < 2,
          text: Translate.get("MergeOrders"),
          disabled: !result.canMerge,
          disabledTooltip: disabledTooltip,
          onClick: () => setOrdersToMerge(orders),
        });
      }

      return menudata;
    },
    [canMergeOrders],
  );

  const columns = useMemo(
    () => [
      {
        field: "regDate",
        type: "date",
        flex: 0.5,
        hide: true,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        headerName: Translate.get("Date"),
        valueGetter: (value, row, column, apiRef) => new Date(value),
        renderHeader: () => <IconAndLabelColumnHeader icon={CalendarTodayIcon} label={Translate.get("Date")} />,
        renderCell: (params) => formatDate(params.value),
      },
      {
        field: "pickDate",
        ...getGridColDefForDateRange(),
        flex: 0.5,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        headerName: Translate.get("PickDate"),
        valueGetter: (value, row, column, apiRef) => (value ? new Date(value) : ""),
        renderHeader: () => <IconAndLabelColumnHeader icon={CalendarTodayIcon} label={Translate.get("PickDate")} />,
        renderCell: (params) => (params.value ? formatDate(params.value) : ""),
      },
      ...(!isSmallScreen
        ? [
            {
              field: "orderNo",
              flex: 0.3,
              disableColumnMenu: true,
              headerClassName: "hideSeparator",
              headerName: Translate.get("OrderNo"),
              renderHeader: () => (
                <IconAndLabelColumnHeader icon={FormatListNumberedIcon} label={Translate.get("OrderNo")} />
              ),
            },
            {
              field: "customerOrderNo",
              flex: 0.5,
              disableColumnMenu: true,
              headerClassName: "hideSeparator",
              headerName: Translate.get("PurchaseOrderNo"),
              renderHeader: () => (
                <IconAndLabelColumnHeader icon={ListAltIcon} label={Translate.get("PurchaseOrderNo")} />
              ),
            },
            {
              field: "invoiceNo",
              flex: 0.3,
              disableColumnMenu: true,
              headerClassName: "hideSeparator",
              headerName: Translate.get("InvoiceNo"),
              renderHeader: () => <IconAndLabelColumnHeader icon={Pin} label={Translate.get("InvoiceNo")} />,
            },
          ]
        : []),
      ...(!actorId
        ? [
            {
              field: "actorName",
              flex: 0.7,
              disableColumnMenu: true,
              headerClassName: "hideSeparator",
              headerName: Translate.get("Company"),
              renderHeader: () => <IconAndLabelColumnHeader icon={DomainIcon} label={Translate.get("Company")} />,
              renderCell: (params) => <Box onClick={() => navigateToCompany(params.row.actorId)}>{params.value}</Box>,
            },
          ]
        : []),
      {
        field: "partDescription",
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        headerName: Translate.get("Product"),
        valueGetter: (value, row, column, apiRef) =>
          row.orderLines &&
          row.orderLines
            .map((ol) =>
              hasAccess(accessKeys.isMASystem)
                ? `${ol.qty} x ${ol.part.description.trim()}`
                : ol.part.description.trim(),
            )
            .join(", "),
        renderHeader: () => <IconAndLabelColumnHeader icon={ShoppingBasketIcon} label={Translate.get("Product")} />,
      },
      ...(!isSmallScreen && hasAccess(accessKeys.isMASystem)
        ? [
            {
              field: "invoicePrice",
              type: "number",
              align: "left",
              headerAlign: "left",
              flex: 0.35,
              filterable: false,
              disableColumnMenu: true,
              headerClassName: "hideSeparator",
              headerName: Translate.get("Price"),
              renderHeader: () => <IconAndLabelColumnHeader icon={Money} label={Translate.get("Price")} />,
              renderCell: (params) =>
                params.row.currency ? formatMoney(params.value, null, params.row.currency) : `${params.value} (?)`,
            },
          ]
        : []),
      // !hasAccess(accessKeys.isMASystem) because MA-system will have qty under product col instead
      ...(!isSmallScreen && !hasAccess(accessKeys.isMASystem)
        ? [
            {
              field: "quantity",
              type: "number",
              align: "left",
              headerAlign: "left",
              flex: 0.5,
              disableColumnMenu: true,
              headerClassName: "hideSeparator",
              headerName: Translate.get("Quantity"),
              valueGetter: (value, row, column, apiRef) =>
                row.orderLines && row.orderLines.map((ol) => ol.qty).join(", "),
              renderHeader: () => <IconAndLabelColumnHeader icon={ScatterPlotIcon} label={Translate.get("Quantity")} />,
            },
          ]
        : []),
      {
        ...getGridColDefForSingleSelect(getStatusFilterOptions(), true),
        field: "status",
        flex: 0.3,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        headerName: Translate.get("Status"),
        renderHeader: () => <IconAndLabelColumnHeader icon={CheckCircleIcon} label={Translate.get("Status")} />,
        display: "flex",
        renderCell: (params) =>
          getOrderIconbox(
            params.value,
            params.row.paymentStatus,
            null,
            false,
            params.row.isCourse,
            params.row.isMissingInfo,
            params.row.scheduledInvoiceDate,
            params.row.invoiceCanceled,
          ) ?? "",
      },
      {
        ...getGridColDefForSingleSelect(getStatusFilterOptions(), true),
        field: "paymentStatus",
        flex: 0.3,
        hide: true,
        hideable: false,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        headerName: Translate.get("PrepayStatus"),
        renderHeader: () => <IconAndLabelColumnHeader icon={CheckCircleIcon} label={Translate.get("PrepayStatus")} />,
        display: "flex",
        renderCell: (params) => "",
      },
      ...(hasAccess(accessKeys.isMASystem)
        ? [
            {
              field: "priority",
              flex: 0.3,
              hide: true,
              headerName: Translate.get("Priority"),
              disableColumnMenu: true,
              headerClassName: "hideSeparator",
              renderHeader: () => <IconAndLabelColumnHeader icon={Flag} label={Translate.get("Priority")} />,
              renderCell: (params) =>
                params.value &&
                params.value !== priority.normal && (
                  <Tooltip title={Translate.getPriority(params.value)}>{getIcon(params.value)}</Tooltip>
                ),
            },
          ]
        : []),
      ...(!isSmallScreen
        ? [
            {
              field: "regActorName",
              flex: 0.5,
              disableColumnMenu: true,
              headerClassName: "hideSeparator",
              headerName: Translate.get("RegisteredBy"),
              renderHeader: () => (
                <IconAndLabelColumnHeader icon={AccountCircleIcon} label={Translate.get("RegisteredBy")} />
              ),
            },
          ]
        : []),
      {
        field: "courseId",
        type: "number",
        hide: true,
        hideable: false,
        flex: 0.3,
        headerName: Translate.get("Course"),
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        renderHeader: () => <IconAndLabelColumnHeader icon={School} label={Translate.get("Course")} />,
      },
      {
        field: "isCourse",
        type: "boolean",
        flex: 0.3,
        hide: true,
        headerName: Translate.get("CourseBooking") + "?",
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        renderHeader: () => <IconAndLabelColumnHeader icon={School} label={Translate.get("CourseBooking") + "?"} />,
        renderCell: (params) => params.value && <Check />,
      },
      {
        field: "isMissingInfo",
        type: "boolean",
        flex: 0.3,
        hide: true,
        headerName: Translate.get("OrderStatus_MissingInfo") + "?",
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        renderHeader: () => (
          <IconAndLabelColumnHeader icon={Warning} label={Translate.get("OrderStatus_MissingInfo") + "?"} />
        ),
        renderCell: (params) => params.value && <Check />,
      },
      {
        field: "invoiceCanceled",
        type: "boolean",
        flex: 0.3,
        hide: true,
        headerName: Translate.get("InvoicedCanceledInERP") + "?",
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        renderHeader: () => (
          <IconAndLabelColumnHeader icon={MoneyOff} label={Translate.get("InvoicedCanceledInERP") + "?"} />
        ),
        renderCell: (params) => params.value && <Check />,
      },
      {
        field: "scheduledInvoiceDate",
        flex: 0.3,
        hide: true,
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        headerName: Translate.getOrderStatus(orderStatus.scheduledForInvoice),
        renderHeader: () => (
          <IconAndLabelColumnHeader icon={Event} label={Translate.getOrderStatus(orderStatus.scheduledForInvoice)} />
        ),
        renderCell: (params) => (params.value ? formatDate(params.value) : ""),
      },
      {
        ...getGridColDefForArrayValues(getPartTypesFilterOptions()),
        field: "partTypes",
        flex: 0.3,
        hide: true,
        headerName: Translate.get("PartTypeLabel"),
        disableColumnMenu: true,
        headerClassName: "hideSeparator",
        renderHeader: () => <IconAndLabelColumnHeader icon={Category} label={Translate.get("PartTypeLabel")} />,
        valueFormatter: (value, row, column, apiRef) =>
          value && Array.isArray(value) && value.map((partType) => Translate.getPartType(partType)).join(", "),
      },
      {
        field: "dotMenu",
        flex: 0.3,
        headerName: "",
        sortable: false,
        disableColumnMenu: true,
        filterable: false,
        hideable: false,
        headerClassName: "hideSeparator",
        align: "right",
        display: "flex",
        renderCell: (params) => <DotMenu content={getDotMenuActions(params.row, false)} horizontal />,
      },
    ],
    [getDotMenuActions, navigateToCompany, actorId, isSmallScreen],
  );

  const easyFilters = useMemo(
    () =>
      defaultFilters.map((x) => ({
        ...x,
        name: Translate.get(x.nameTranslationKey),
        description: Translate.get(x.descriptionTranslationKey),
        nameTranslationKey: null,
        descriptionTranslationKey: null,
      })),
    [],
  );

  async function handleMultiDotMenuActionOnClick(actions) {
    // kör synkoront för att undvika överbelastning av backend
    const results = [];
    for (const action of actions) {
      const result = await action.onClick();
      results.push({ ...result, orderId: action.orderId });
    }

    const failedResults = results.filter((r) => !r.isSuccessful);
    const failedResultsAndOrders = failedResults.map((r) => ({
      result: r,
      order: pagination.rows.find((order) => order.orderId === r.orderId),
    }));
    if (failedResultsAndOrders.length > 0) {
      const errorMessage = failedResultsAndOrders
        .map(({ result, order }) => {
          const messages = [];
          if (result.errorMessageTranslationKey) {
            messages.push(Translate.get(result.errorMessageTranslationKey));
          }
          if (result.userMessage) {
            messages.push(result.userMessage);
          }
          if (messages.length < 1) {
            messages.push(Translate.get("SomethingFailed"));
          }

          const combinedErrorMessages = messages.join("\n");

          return `${order.orderNo}, ${order.actorName} (${formatDate(order.pickDate)}):\n${combinedErrorMessages}`;
        })
        .join("\n\n");
      setLongErrorMessage(errorMessage);
    } else {
      // alla gick bra
      const successMessage = enqueueSnackbar(Translate.get("Ready"), {
        variant: "success",
        autoHideDuration: 6000,
        onClick: () => closeSnackbar(successMessage),
      });
    }

    if (results.some((r) => r.isSuccessful)) {
      setSelectedOrderIds({ type: "include", ids: new Set() });
      await reloadData();
    }
  }

  function getSelectionActions() {
    const selectedOrders = pagination.rows.filter((order) => selectedOrderIds.ids.has(order.orderId));
    const numberOfSelectedOrders = selectedOrders.length;

    // We get the normal per-order dot menu actions and group them
    // for simultaneous exection of each action individually
    const dotMenuActions = selectedOrders
      .flatMap((order) =>
        getDotMenuActions(order, true, numberOfSelectedOrders).map((action) => ({
          ...action,
          orderId: order.orderId,
        })),
      )
      .filter((action) => !action.hide);
    const groupedActions = groupBy(dotMenuActions, (action) => action.id);
    const requiresFreight = selectedOrders.some((order) => mayNeedFreightBeforeInvoice(order));

    const mergedActions = Object.entries(groupedActions).map(([id, actions]) => ({
      icon: actions[0].icon,
      text: actions[0].text,
      onClick: async () => await handleMultiDotMenuActionOnClick(actions),
      disabled: actions.length !== selectedOrders.length || actions.some((a) => a.disabled),
      disabledTooltip: Translate.get("GroupActionUnavailableForSome"),
      verifyActionQuestion: requiresFreight ? actions[0].multiVerifyActionQuestion : null,
    }));

    // Then we also have some actions wich are specifically for the multi-order
    // scenario, i.e. these are not available in an individual orders dot menu
    const multiActions = getMultiMenuActions(selectedOrders);
    return [...mergedActions, ...multiActions];
  }

  const getDetailPanelContent = useCallback(
    ({ row }) => <OrdersGridDetailsPanel orderLines={row.orderLines} currency={row.currency} />,
    [],
  );

  return (
    <>
      {ordersToMerge && ordersToMerge.length > 0 && (
        <MergeOrdersDialog
          orders={ordersToMerge}
          onClose={() => setOrdersToMerge(null)}
          onMerged={() => {
            setOrdersToMerge(null);
            reloadData();
          }}
        />
      )}

      {editOrderDetails && (
        <Dialog maxWidth={false} scroll="paper" open={true}>
          <OrderFormBase
            orderId={editOrderDetails.orderId}
            onClose={() => {
              setEditOrderDetails(null);
              reloadData();
            }}
          />
        </Dialog>
      )}

      {pickOrderDetails && (
        <PickOrderDialog
          orderId={pickOrderDetails.orderId}
          startPick={!pickOrderDetails.hasStartedPick}
          onClose={() => {
            setPickOrderDetails(null);
            reloadData();
          }}
          invoiceOrder={async (order) => {
            const response = await handleInvoiceOrder(order, false, false);
            if (response?.isSuccessful) {
              setPickOrderDetails(null);
            }
          }}
        />
      )}

      {scheduleOrderInvoiceDetails && (
        <OrderScheduleForInvoiceDialog
          currentScheduledInvoiceDate={scheduleOrderInvoiceDetails.scheduledInvoiceDate}
          orderId={scheduleOrderInvoiceDetails.orderId}
          onClose={handleScheduleInvoiceClose}
        />
      )}

      {hasAccess(accessKeys.isMASystem) && !actorId && (
        <Box paddingTop={2} paddingLeft={1} paddingBottom={1}>
          <DataGridEasyFilters
            filters={easyFilters}
            currentFilterModel={pagination.filterModel}
            onSelectFilter={(filter) => {
              pagination.onFilterModelChange(filter?.filterModel);
              pagination.onSortModelChange(filter?.sortModel);
            }}
            summaryFilterIds={[
              "default-picking",
              "default-picking-licenses",
              "default-picking-books",
              "default-picking-misc",
              "default-ready-for-invoice-filter",
              "default-scheduled-for-invoice-filter",
              "default-missing-info",
              "default-invoice-canceled",
            ]}
            summaryUpdateUrl={`${import.meta.env.VITE_MAIN_URL}orders/get-row-counts`}
            refreshTimeStamp={refreshTimeStamp}
            actorId={actorId}
          />
        </Box>
      )}
      {selectedOrderIds && selectedOrderIds.ids?.size > 0 && (
        <Toolbar className="orderToolBar">
          <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box>{`${selectedOrderIds.ids.size} ${Translate.get("ePort2Selected")}`}</Box>
              <LinkMenu content={getSelectionActions()} />
            </Stack>
            <Tooltip title={Translate.get("DeselectAll")}>
              <IconButton onClick={() => setSelectedOrderIds({ type: "include", ids: new Set() })}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      )}
      <DataGrid
        showToolbar
        checkboxSelection
        apiRef={apiRef}
        className={gridClassName}
        columns={columns}
        getRowId={(row) => row.orderId}
        autoHeight={true}
        slotProps={{
          columnsPanel: { className: "customColumnPanel" },
          filterPanel: { className: "customfilterPanel" },
          panel: { className: "customPanel" },
        }}
        localeText={localizedTextsMap}
        disableDensitySelector={true}
        disableColumnMenu
        onRowSelectionModelChange={(newSelectedOrderIds) => {
          setSelectedOrderIds(newSelectedOrderIds);
        }}
        rowSelectionModel={selectedOrderIds}
        hideFooterSelectedRowCount
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={() => "auto"}
        slots={{
          toolbar: () => ToolBar({ showExport: true, apiRef, exportFileName: Translate.get("Orders") }),
          detailPanelExpandIcon: KeyboardArrowRight,
          detailPanelCollapseIcon: KeyboardArrowDown,
          noRowsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHere")),
        }}
        initialState={{
          columns: {
            columnVisibilityModel: {
              regDate: false,
              courseId: false,
              isCourse: false,
              isMissingInfo: false,
              partTypes: false,
              paymentStatus: false,
              scheduledInvoiceDate: false,
              invoiceCanceled: false,
            },
          },
        }}
        {...pagination}
      />
      <YesOrNoDialog
        open={!!invoiceOrderQuestionDetails}
        title={Translate.get("Invoice")}
        text={Translate.get("InvoiceOrderWithPhysicalPickNoFreight")}
        onYes={async () => {
          await handleInvoiceOrder(invoiceOrderQuestionDetails.order, false, true);
          setInvoiceOrderQuestionDetails(null);
          setPickOrderDetails(null); // Needed if it was called from PickOrderDialog
        }}
        onNo={() => setInvoiceOrderQuestionDetails(null)}
      />
      <YesOrNoDialog
        open={!!showForceCancelOrderId}
        title={Translate.get("HandleInvoiceCanceled")}
        text={Translate.get("HandleInvoiceCanceledDescription")}
        noText={Translate.get("Cancel")}
        yesText={Translate.get("CancelOrder")}
        onYes={async () => {
          await forceCancelOrder(showForceCancelOrderId);
          setShowForceCancelOrderId(null);
        }}
        onNo={() => setShowForceCancelOrderId(null)}
      />
      <AlertDialog
        titleText={Translate.get("SomethingFailed")}
        bodyText={longErrorMessage}
        buttonText={Translate.get("Ok")}
        open={!!longErrorMessage}
        onClose={() => {
          setLongErrorMessage(null);
          reloadData();
        }}
      />
    </>
  );
}

function mayNeedFreightBeforeInvoice(order) {
  // We ignore card print even if it's physical pick, since these rarely need freight
  const hasPhysicalPick = order.orderLines.some(
    (ol) => isPhysicalPickPartType(ol.part.partType) && !isCardPrintPartType(ol.part.partType),
  );
  const hasFreight = order.orderLines.some((ol) => isFreightPartType(ol.part.partType));
  const isCustomerPickup = order.modeOfTransport === 13 || order.modeOfTransport === 70;

  return hasPhysicalPick && !hasFreight && !isCustomerPickup;
}
